import { Form } from 'react-bootstrap';
import { FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/app/hook';
import ProBadge from '@/components/ui/ProBadge';
import usePermission from '@/hooks/usePermission';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { handleResetAppearance, handleUpdateAppearanceItem } from './appearanceSlice';
import { IAiAppearance } from './types';

type Props = {
  register: UseFormRegister<IAiAppearance>;
  errors: FieldErrors<IAiAppearance>;
  watch: UseFormWatch<IAiAppearance>;
};

function AppearanceDownTime({ register, errors, watch }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const permissions = usePermission();

  return (
    <>
      <Form.Group className="tw-flex tw-items-center tw-gap-2">
        <Form.Check
          type="switch"
          id="hideCloseButton"
          disabled={!permissions.settingsPage.widget.customization.hideCloseButton}
          className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
          label={t('DashboardSettingCustomizeChat/l-20')}
          isInvalid={!!errors.hideCloseButton}
          feedbackType="invalid"
          feedback={errors.hideCloseButton?.message}
          {...register('hideCloseButton', {
            onChange: e => {
              dispatch(
                handleUpdateAppearanceItem({
                  key: 'hideCloseButton',
                  value: e.target.checked,
                })
              );
            },
          })}
        />
        {!permissions.settingsPage.widget.customization.hideCloseButton && (
          <ProBadge
            className="tw-ms-2"
            onClose={() => {
              dispatch(handleResetAppearance());
            }}
          />
        )}
      </Form.Group>

      {!watch('hideCloseButton') && (
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex">
          <Form.Group className="tw-w-full  tw-flex  tw-flex-col ">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t('DashboardSettingCustomizeChat/l-24')}
            </Form.Label>

            <Form.Control
              isInvalid={!!errors.closeWidgetDuration}
              maxLength={30}
              max={300}
              {...register('closeWidgetDuration', {
                onChange: event => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'closeWidgetDuration',
                      value: event.target.value != '' ? event.target.value : null,
                    })
                  );
                },
              })}
              placeholder={'0'}
              type={'number'}
              className="tw-h-10 tw-w-32 tw-px-4 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex"
            />
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors?.closeWidgetDuration?.message as string}
            </Form.Control.Feedback>
          </Form.Group>
        </div>
      )}

      <Form.Group className="tw-flex tw-items-center tw-gap-2">
        <Form.Check
          type="switch"
          id="hideMinimizeButton"
          disabled={!permissions.settingsPage.widget.customization.hideCloseButton}
          className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
          label={t('DashboardSettingCustomizeChat/l-25')}
          isInvalid={!!errors.hideMinimizeButton}
          feedbackType="invalid"
          feedback={errors.hideMinimizeButton?.message}
          {...register('hideMinimizeButton', {
            onChange: e => {
              dispatch(
                handleUpdateAppearanceItem({
                  key: 'hideMinimizeButton',
                  value: e.target.checked,
                })
              );
            },
          })}
        />
        {!permissions.settingsPage.widget.customization.hideCloseButton && (
          <ProBadge
            className="tw-ms-2"
            onClose={() => {
              dispatch(handleResetAppearance());
            }}
          />
        )}
      </Form.Group>
    </>
  );
}

export default WrappedErrorBoundary(AppearanceDownTime);
